/**
 * Profile Likelihood Confidence Intervals for Weibull Distribution
 * Statistically robust alternative to Fisher Information Matrix for highly censored data
 */

import { MLEDataPoint, logLikelihood } from './weibullMLE';

export interface ProfileCIResult {
  shape: { lower: number; upper: number };
  scale: { lower: number; upper: number };
  method: string;
  diagnostics: {
    chi2Critical: number;
    logLThreshold: number;
    searchSteps: number;
  };
}

/**
 * Calculate Profile Likelihood Confidence Intervals for Weibull parameters
 * Uses likelihood ratio test: 2*(logL_max - logL(θ)) ≤ χ²(1, α)
 */
export function calculateProfileLikelihoodCI(
  maxLogL: number,
  shapeEst: number,
  scaleEst: number,
  data: MLEDataPoint[],
  confidenceLevel: number = 0.95
): ProfileCIResult {
  const chi2Critical = getChiSquaredCritical(confidenceLevel);
  const logLThreshold = maxLogL - chi2Critical / 2;
  
  const diagnostics = {
    chi2Critical,
    logLThreshold,
    searchSteps: 0
  };
  
  // SHAPE PARAMETER BOUNDS (keep scale fixed)
  let shapeLower = shapeEst;
  let shapeUpper = shapeEst;
  
  // Lower bound: systematic decrease
  for (let factor = 0.99; factor >= 0.1; factor -= 0.01) {
    const testShape = shapeEst * factor;
    const testLogL = logLikelihood(testShape, scaleEst, data);
    
    diagnostics.searchSteps++;
    
    if (testLogL >= logLThreshold) {
      shapeLower = testShape;
    } else {
      break;
    }
  }
  
  // Upper bound: systematic increase
  for (let factor = 1.01; factor <= 5.0; factor += 0.01) {
    const testShape = shapeEst * factor;
    const testLogL = logLikelihood(testShape, scaleEst, data);
    
    diagnostics.searchSteps++;
    
    if (testLogL >= logLThreshold) {
      shapeUpper = testShape;
    } else {
      break;
    }
  }
  
  // SCALE PARAMETER BOUNDS (keep shape fixed)
  let scaleLower = scaleEst;
  let scaleUpper = scaleEst;
  
  // Lower bound: systematic decrease  
  for (let factor = 0.99; factor >= 0.1; factor -= 0.01) {
    const testScale = scaleEst * factor;
    const testLogL = logLikelihood(shapeEst, testScale, data);
    
    diagnostics.searchSteps++;
    
    if (testLogL >= logLThreshold) {
      scaleLower = testScale;
    } else {
      break;
    }
  }
  
  // Upper bound: systematic increase
  for (let factor = 1.01; factor <= 5.0; factor += 0.01) {
    const testScale = scaleEst * factor;
    const testLogL = logLikelihood(shapeEst, testScale, data);
    
    diagnostics.searchSteps++;
    
    if (testLogL >= logLThreshold) {
      scaleUpper = testScale;
    } else {
      break;
    }
  }
  
  const shapeBounds = { lower: shapeLower, upper: shapeUpper };
  const scaleBounds = { lower: scaleLower, upper: scaleUpper };
  
  return {
    shape: shapeBounds,
    scale: scaleBounds,
    method: 'Profile Likelihood',
    diagnostics
  };
}

/**
 * Get critical value from chi-squared distribution
 */
function getChiSquaredCritical(confidenceLevel: number): number {
  switch (confidenceLevel) {
    case 0.90: return 2.706;
    case 0.95: return 3.841;
    case 0.99: return 6.635;
    default: return 3.841;
  }
}

/**
 * Calculate robust confidence intervals with automatic fallback
 * Prefers Profile Likelihood for highly censored data, with conservative fallback
 */
export function confidenceIntervals(
  maxLogL: number,
  shape: number,
  scale: number, 
  data: MLEDataPoint[],
  confidenceLevel: number = 0.95
): {
  shape: { lower: number; upper: number };
  scale: { lower: number; upper: number };
  method: string;
  fallbackUsed?: boolean;
} {
  
  const censoringRate = data.filter(p => p.isCensored).length / data.length;
  const failureCount = data.filter(p => !p.isCensored).length;
  
  try {
    // Use Profile Likelihood for highly censored data
    if (censoringRate > 0.3 || failureCount < 10) {
      const profileResult = calculateProfileLikelihoodCI(maxLogL, shape, scale, data, confidenceLevel);
      
      // Validate results are reasonable (not zero-width)
      const shapeWidth = profileResult.shape.upper - profileResult.shape.lower;
      const scaleWidth = profileResult.scale.upper - profileResult.scale.lower;
      
      if (shapeWidth > 0.01 && scaleWidth > 0.01) {
        return {
          shape: profileResult.shape,
          scale: profileResult.scale,
          method: profileResult.method
        };
      } else {
        throw new Error(`Profile Likelihood produced degenerate intervals: shape width=${shapeWidth.toFixed(6)}, scale width=${scaleWidth.toFixed(6)}`);
      }
    } else {
      // For low censoring, could use Fisher Information Matrix (not implemented here)
      throw new Error('Low censoring detected - Fisher Information Matrix recommended but not available in this utility');
    }
    
  } catch (error) {
    console.warn('Profile Likelihood failed, using conservative fallback:', error instanceof Error ? error.message : 'Unknown error');
    
    // Conservative uncertainty estimation for highly censored reliability data
    const baseShapeUncertainty = 0.3; // 30% base uncertainty for shape
    const baseScaleUncertainty = 0.25; // 25% base uncertainty for scale
    
    // Increase uncertainty for high censoring and small samples
    const censoringPenalty = 1 + 2 * censoringRate; // More censoring = more uncertainty
    const sampleSizePenalty = Math.sqrt(Math.max(1.0, 15.0 / failureCount)); // Fewer failures = more uncertainty
    
    const effectiveShapeStd = shape * baseShapeUncertainty * censoringPenalty * sampleSizePenalty;
    const effectiveScaleStd = scale * baseScaleUncertainty * censoringPenalty * sampleSizePenalty;
    
    const zCritical = confidenceLevel === 0.99 ? 2.576 : 
                     confidenceLevel === 0.90 ? 1.645 : 1.96;
    
    return {
      shape: {
        lower: Math.max(0.01, shape - zCritical * effectiveShapeStd),
        upper: shape + zCritical * effectiveShapeStd
      },
      scale: {
        lower: Math.max(0.01, scale - zCritical * effectiveScaleStd), 
        upper: scale + zCritical * effectiveScaleStd
      },
      method: 'Conservative (highly censored data)',
      fallbackUsed: true
    };
  }
}
